import React, { MutableRefObject, useCallback, useEffect } from 'react';
import useResizeObserver from '@/components/_hooks/useResizeObserver';

/**
 * useScrollGradient
 *
 * A utility hook for scrollable containers that tells you when to show
 * top and bottom fade gradients based on the scroll position.
 *
 * It takes two refs:
 * - wrapperRef: the scrollable outer container (`overflow-y: auto` element)
 * - innerWrapperRef: the inner content wrapper whose size may change
 *
 * The hook:
 * - Tracks whether the content is not scrollable, at the top, in the middle, or at the bottom.
 * - Listens to scroll events and uses resize observers on both wrapper and inner content
 *   to keep the state in sync when layout or content height changes.
 *
 * It returns:
 * - isStartGradientVisible: `true` when the user is not at the very top
 *   and the content is scrollable (show top gradient).
 * - isEndGradientVisible: `true` when the user is not at the very bottom
 *   and the content is scrollable (show bottom gradient).
 */

export const useScrollGradient = (wrapperRef: MutableRefObject<HTMLDivElement | null>, innerWrapperRef: MutableRefObject<HTMLDivElement | null>) => {
  const [scrollPosition, setScrollPosition] = React.useState<'no-scroll' | 'top' | 'middle' | 'bottom'>('no-scroll');

  const checkScrollPosition = useCallback(() => {
    if (!wrapperRef.current) return;
    const { scrollTop, clientHeight, scrollHeight } = wrapperRef.current;

    if (scrollHeight <= clientHeight) {
      setScrollPosition('no-scroll');
    } else if (scrollTop === 0) {
      if (scrollPosition !== 'top') setScrollPosition('top');
    } else if (clientHeight + scrollTop >= scrollHeight - 1) {
      if (scrollPosition !== 'bottom') setScrollPosition('bottom');
    } else if (scrollPosition !== 'middle') {
      setScrollPosition('middle');
    }
  }, [scrollPosition]);

  useEffect(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);

  useEffect(() => {
    if (wrapperRef.current) wrapperRef.current.addEventListener('scroll', checkScrollPosition);
    return () => {
      if (wrapperRef.current) wrapperRef.current.removeEventListener('scroll', checkScrollPosition);
    };
  }, [wrapperRef.current, checkScrollPosition]);

  useResizeObserver({ ref: wrapperRef, onResize: checkScrollPosition });
  useResizeObserver({ ref: innerWrapperRef, onResize: checkScrollPosition });

  return {
    isStartGradientVisible: !['no-scroll', 'top'].includes(scrollPosition),
    isEndGradientVisible: !['no-scroll', 'bottom'].includes(scrollPosition)
  };
};
